import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  FiCalendar,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import moment from 'moment-timezone';
import classNames from 'classnames';
import { Portal } from '@components/Portal';
import styles from './SingleDatePicker.module.scss';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getSubLabel(dateStr) {
  const m = moment(dateStr, 'YYYY-MM-DD');
  if (m.isSame(moment(), 'day')) return 'Today';
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  return m.format('dddd');
}

function buildCalendarGrid(year, month) {
  const start = moment({ year, month }).startOf('week');
  return Array.from({ length: 42 }, (_, i) => start.clone().add(i, 'days'));
}

export const SingleDatePicker = ({
  date,
  onDateChange,
  compact,
  disableFutureDates = true,
  placeholder = 'Select date',
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = useMemo(
    () => (date ? moment(date, 'YYYY-MM-DD') : null),
    [date]
  );
  const today = useMemo(() => moment().startOf('day'), []);
  const [viewYear, setViewYear] = useState((selected || today).year());
  const [viewMonth, setViewMonth] = useState((selected || today).month());
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const onOutsideClick = (e) => {
      const clickedTrigger =
        containerRef.current && containerRef.current.contains(e.target);
      const clickedDropdown =
        dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!clickedTrigger && !clickedDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, [isOpen]);

  // Positioned in a Portal (see render below) so the calendar always escapes
  // any clipping ancestor (e.g. a Modal's own overflow/scroll container)
  // instead of being cropped by it - the standard fix for popovers rendered
  // inside scrollable/overflow-hidden containers.
  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Sync view when date prop changes externally
  useEffect(() => {
    const reference = selected || today;
    setViewYear(reference.year());
    setViewMonth(reference.month());
  }, [selected, today]);

  const days = useMemo(
    () => buildCalendarGrid(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const handleDayClick = useCallback(
    (day) => {
      if (disableFutureDates && day.isAfter(today)) return;
      onDateChange(day.format('YYYY-MM-DD'));
      setIsOpen(false);
    },
    [onDateChange, today, disableFutureDates]
  );

  const handlePreset = useCallback(
    (dayMoment) => {
      onDateChange(dayMoment.format('YYYY-MM-DD'));
      setIsOpen(false);
    },
    [onDateChange]
  );

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <div
        className={classNames(
          compact ? styles.triggerCompact : styles.trigger,
          {
            [styles.triggerDisabled]: disabled,
          },
          className
        )}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) =>
          !disabled && e.key === 'Enter' && setIsOpen((o) => !o)
        }
      >
        <FiCalendar size={compact ? 16 : 20} className={styles.calendarIcon} />
        {compact ? (
          <span
            className={classNames(styles.dateTextCompact, {
              [styles.dateTextPlaceholder]: !selected,
            })}
          >
            {selected ? selected.format('DD MMM YYYY') : placeholder}
          </span>
        ) : (
          <div className={styles.textGroup}>
            <span className={styles.dateLabel}>Date</span>
            <span
              className={classNames(styles.dateText, {
                [styles.dateTextPlaceholder]: !selected,
              })}
            >
              {selected ? selected.format('MMM DD, YYYY') : placeholder}
            </span>
          </div>
        )}
        <FiChevronDown
          size={14}
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
        />
      </div>

      {isOpen && !disabled && dropdownPosition && (
        <Portal>
          <div
            className={styles.dropdown}
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              right: dropdownPosition.right,
            }}
          >
            {/* Month navigation */}
            <div className={styles.calHeader}>
              <button
                className={styles.navBtn}
                onClick={prevMonth}
                type="button"
              >
                <FiChevronLeft size={13} />
              </button>
              <span className={styles.monthLabel}>
                {moment({ year: viewYear, month: viewMonth }).format(
                  'MMMM YYYY'
                )}
              </span>
              <button
                className={styles.navBtn}
                onClick={nextMonth}
                type="button"
              >
                <FiChevronRight size={13} />
              </button>
            </div>

            {/* Weekday headers */}
            <div className={styles.weekdays}>
              {WEEKDAYS.map((d) => (
                <span key={d} className={styles.weekday}>
                  {d}
                </span>
              ))}
            </div>

            {/* Day grid */}
            <div className={styles.daysGrid}>
              {days.map((day, i) => {
                const isCurrentMonth = day.month() === viewMonth;
                const isSelected = !!selected && day.isSame(selected, 'day');
                const isToday = day.isSame(today, 'day');
                const isFuture = disableFutureDates && day.isAfter(today);

                let cls = styles.day;
                if (!isCurrentMonth) cls += ` ${styles.dayOtherMonth}`;
                if (isSelected) cls += ` ${styles.daySelected}`;
                else if (isToday) cls += ` ${styles.dayToday}`;
                if (isFuture) cls += ` ${styles.dayDisabled}`;

                return (
                  <button
                    key={i}
                    type="button"
                    className={cls}
                    onClick={() => handleDayClick(day)}
                    disabled={isFuture}
                  >
                    {day.date()}
                  </button>
                );
              })}
            </div>

            {/* Quick presets */}
            <div className={styles.presets}>
              <button
                type="button"
                className={styles.preset}
                onClick={() => handlePreset(moment())}
              >
                Today
              </button>
              <button
                type="button"
                className={styles.preset}
                onClick={() => handlePreset(moment().subtract(1, 'day'))}
              >
                Yesterday
              </button>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};
